// src/hooks/useBackNavigation.js
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";

/**
 * Custom hook for predictable back navigation.
 * 
 * Usage:
 *   const goBack = useBackNavigation("/default/path");
 *   <Button onClick={goBack}>Back</Button>
 * 
 * Or with explicit fallback:
 *   const goBack = useBackNavigation();
 *   <Button onClick={() => goBack("/properties")}>Back</Button>
 * 
 * The hook checks:
 * 1. If location.state.from exists (passed when navigating to this page)
 * 2. Falls back to the provided default path
 * 3. Falls back to /dashboard if nothing else
 */
export function useBackNavigation(defaultPath = "/dashboard") {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback(
    (fallbackPath) => {
      // Check if we have a "from" path in location state
      const fromPath = location.state?.from;
      
      if (fromPath) {
        navigate(fromPath, { replace: true });
      } else if (fallbackPath) {
        navigate(fallbackPath, { replace: true });
      } else if (defaultPath) {
        navigate(defaultPath, { replace: true });
      } else {
        // Last resort: go to dashboard
        navigate("/dashboard", { replace: true });
      }
    },
    [navigate, location.state, defaultPath]
  );

  return goBack;
}

/**
 * Helper to create navigation props that track the "from" path.
 * 
 * Usage:
 *   navigate(`/lpe1/${id}`, navigateWithFrom(location));
 */
export function createNavigateState(currentPath) {
  return {
    state: { from: currentPath },
  };
}

export default useBackNavigation;


